/**
 * Default-response override schemas (decision D25, 2026-08-13) — a pack's
 * claim on the engine's fallback voice.
 *
 * @remarks
 * The engine ships a complete built-in table of generic lines — every
 * refusal, teach, and acknowledgement it can ever need to speak when content
 * is silent — so every game works with ZERO authoring here. This schema is
 * the override surface: a pack may re-voice any subset of those slots with
 * its own `NarrativeLine` (text AND transcript source), and the engine falls
 * back to its built-in for the rest. Baked in, always claimable.
 *
 * This is how Orpheus Protocol makes the ask deflection EURYDICE's without
 * the ENGINE ever knowing she exists (the R8-era engine default spoke as
 * her — a named character of one game haunting every future game; D25 ends
 * that), and how any pack can give its failure paths a voice — the
 * Zork-lineage "hollow voice" as a platform feature.
 *
 * **Placeholders.** Override texts may embed the slot's documented
 * placeholders — `{abandoned}`, `{damage}`, `{directives}`, `{health}`,
 * `{intent}`, `{itemName}`, `{itemNames}`, `{joiner}`, `{suggestions}`,
 * `{tokens}`, `{verb}` — which the engine
 * substitutes deterministically at speak time (unknown braces pass through
 * untouched). `{suggestions}`, when non-empty, arrives with its own leading
 * space; an empty substitution has its surrounding whitespace swept, so a slot
 * whose `{joiner}` came back empty does not ship a double space.
 *
 * `{intent}` SHOUTS a directive name ("Nothing about the kettle answers to
 * THROW"); `{verb}` is the same word in sentence case, for use inside prose
 * ("You cannot throw the kettle" — or the D78-era teach "To throw what?").
 * Reaching for the wrong one is how "THROW, on the kettle" happened. `{Verb}`
 * (D230) is `{verb}` with its first letter up, for a line that OPENS with the
 * player's word ("Leave it where?"); every slot that offers `{verb}` offers it.
 *
 * **The slot count is not written down anywhere.** It drifted twice in prose
 * before anyone noticed (Calder, 2026-08-19). Derive it from
 * {@link DEFAULT_RESPONSE_SLOTS}; the engine's built-in table is typed
 * `Record<DefaultResponseSlot, …>`, so the compiler already guarantees every
 * slot listed below has exactly one built-in line.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { narrativeLineSchema } from './transcript.js';

/**
 * Every overridable engine response slot, with the placeholders its CALL SITE
 * actually supplies. A placeholder the call site does not supply renders as
 * literal braces in a shipped game, so this list is the authoring contract —
 * keep it in step when a slot is added.
 *
 * - `alreadyHeld` — `{itemName}`
 * - `answerDoesNotServe` — `{itemName}`, `{joiner}`, `{tokens}`, `{verb}` (§3.2/D84: an
 *   answer that RESOLVED and was refused for the object pair — refused in the
 *   question's terms; `{tokens}` is the instrument's authored name)
 * - `answerNotUnderstood` — `{intent}`, `{itemName}`, `{joiner}`, `{tokens}`,
 *   `{verb}` (D38: the failed ANSWER to a parked question)
 * - `askDeflection` — (none; MUST stay generic where a game leans on
 *   absence-is-silence beats — the override re-voices, it never answers)
 * - `bareNounPrompt` — `{itemName}` (D37)
 * - `blankInput` — (none)
 * - `chainAbandoned` — `{abandoned}` (§8.8/D79 tail: a multi-clause input
 *   stopped early — by a refusal, a question, or a failed parse — and the
 *   clauses after the stop were NOT run. One line, once, naming them:
 *   silence was the bug, not the stopping)
 * - `clarificationQuestion` — `{options}` (the which-one prompt the app
 *   renders beside the clarification chips — conversational chrome, never a
 *   transcript entry (the R6 ruling), claimable per D25 so a pack can
 *   re-voice the ask; R17-prep. Since D79 it is also the parked
 *   which-entity question's `questionText`, so the visible surface and the
 *   ask agree by construction. D229 (E14): `{options}` is the offered
 *   answers as one sentence — "The intact locker, or the buckled locker?" —
 *   so the LINE names them, for a screen reader and the terminal, where the
 *   chips are not the record; a pack that leaves the placeholder out still
 *   has them named by the transcript export)
 * - `questionWithdrawn` — (none; D79: a parked question is CONTEXT — leaving
 *   the room it was asked in closes it, and the close says so in one line,
 *   once. One of the two loud exits in the survives-until-answered model)
 * - `questionSetAside` — (none; R34/D114: the player said `never mind` — or
 *   clicked the chip's dismiss, which submits the same words — and the
 *   parked question closes on their own directive. The other loud exit)
 * - `noQuestionToWithdraw` — (none; R34/D114: `never mind` with nothing
 *   parked. No turn consumed either way — withdrawal is conversation
 *   management, not a world action)
 * - `restartConfirm` — `{count}` (D179: the gate before `restart` throws
 *   the descent away; `{count}` is the locations walked, the MAP's number.
 *   OFFERED, not spent: D183 took the number out of the shipped sentence,
 *   because a counted noun in a template says "1 locations" the first time
 *   anybody restarts. A pack that wants the number writes it back in.
 *   Also the parked confirm's `questionText`, so the chips and the ask
 *   agree by construction)
 * - `restartProceedLabel` / `restartKeepLabel` — (none; D179: the two chips'
 *   labels — a slot each, so a pack re-voices the buttons the way it
 *   re-voices a line. The engine folds each label into the words it
 *   accepts, beside `yes` / `no`)
 * - `restartAnswers` — `{proceedLabel}`, `{keepLabel}` (D229, E16: the line
 *   after the confirm that says how it is answered — "Type YES to start
 *   over, or NO to keep it." — because a typed confirm on a terminal or in
 *   a screen reader's ear has no chips. The labels arrive lowercased for the
 *   sentence. D229 also reads the title screen's `restart` block — its
 *   `confirm`, `proceedLabel`, `keepLabel` — as the typed confirm's words
 *   when these slots are unclaimed, so the same act never sounds like two)
 * - `restartKept` — (none; D179: the player kept the descent)
 * - `saveAutomatic` / `loadAutomatic` — (none; D179: `SAVE` / `LOAD` on a
 *   pack that saves itself — the directive exists in every game and says
 *   here that there is nothing for it to do)
 * - `saveWritten` / `loadRestored` — (none; D179: `SAVE` / `LOAD` on a
 *   `manual` pack — the one slot written, or brought back)
 * - `saveNothingKept` / `loadNothingKept` — (none; D231, Calder's P7 §3: the
 *   HOST declared it keeps nothing — a terminal whose state lives in a
 *   closure — so `SAVE` / `LOAD` say so honestly in every pack it hosts,
 *   and no pack has to know where it is hosted)
 * - `loadNothingSaved` — (none; D185: `LOAD` on a `manual` pack when no
 *   slot has been written yet. The host, not the engine, knows whether a
 *   slot exists, so the engine answers `loadRestored` first and the host
 *   re-answers with this one when it finds nothing — Calder's D183 finding
 *   that `loadRestored`'s hedge was covering a missing slot)
 * - `conditionUnmet` — (none)
 * - `correctionConfirm` — `{correction}` (D212: the fuzzy rung read the head
 *   verb as another act; the clause as it would read, put to the player)
 * - `correctionKeepLabel` — (none) (D212: the chip that declines)
 * - `correctionKept` — (none) (D212: said when the player declines)
 * - `correctionProceedLabel` — (none) (D212: the chip that runs the reading)
 * - `entityNotFound` — `{tokens}`, `{suggestions}` (D212: a thing the pack
 *   DECLARES somewhere, not here — "You see no mask here.")
 * - `entityUnknown` — `{tokens}`, `{suggestions}` (D212: a word the pack never
 *   declared a thing by — "What zeppelin?" — the D48 distinction, extended
 *   from questions to every miss; Mike, 2026-09-25)
 * - `entityNotVisible` — (none)
 * - `exitLeadsOutward` — (none)
 * - `gibberishInput` — `{tokens}`, `{suggestions}` (D28)
 * - `help` — `{directives}` (D43: derived from the compiled vocabulary; a
 *   hand-written verb list is a help screen that lies in every other game)
 * - `impliedInstrument` — `{itemName}` (D269: the one carried thing that opens
 *   what the player named, used without being named — "(with the slate)")
 * - `inventoryEmpty` — (none)
 * - `inventoryList` — `{itemNames}`
 * - `malformedCommand` — (none)
 * - `missingDirection` — (none; D37 parks it, so it is a real question)
 * - `missingInstrument` — `{intent}`, `{verb}`, `{Verb}` (D37 parks it; D230
 *   fills the player's verb — "Pry it with what?")
 * - `missingTarget` — `{intent}`, `{verb}`, `{Verb}` (D37 parks it; D230
 *   fills the player's verb, so a pack whose `use` is leaving may ask "Leave
 *   it where?"; `{Verb}` is `{verb}` with its first letter up, for a line
 *   that opens with it, and is offered wherever `{verb}` is)
 * - `noExitThatWay` — (none)
 * - `noIntentRecognized` — `{tokens}`, `{suggestions}`
 * - `noMatchingInteraction` — (none)
 * - `noPathToThat` — `{itemName}` (D31)
 * - `notOpenable` — `{itemName}` (D50: the thing has no door to work, which is
 *   a fact about the thing rather than a refusal about the verb)
 * - `nothingOpensThat` — `{itemName}`, `{verb}` (D39)
 * - `notPortable` — (none)
 * - `questionNotUnderstood` — `{tokens}`, `{suggestions}` (D29)
 * - `switchedOn` / `switchedOff` — `{itemName}` (D56: what flipping a switch
 *   sounds like when the pack authored nothing specific. The generic pair is
 *   deliberately flat: a switch worth writing about gets
 *   `turnOnNarratives` / `turnOffNarratives`, and one that is not should not
 *   be pretending.)
 * - `deviceIsDead` — `{itemName}` (D65: the switch moved and nothing
 *   answered — the power condition is unmet, and the gap between the switch
 *   and the power is the fiction)
 * - `switchAlreadyOff` / `switchAlreadyOn` — `{itemName}` (D120: the switch is
 *   already in the position asked for, so nothing moved and nothing should be
 *   narrated as though it had)
 * - `doorAlreadyClosed` / `doorAlreadyOpen` — `{itemName}` (D135: D120's rule
 *   reaching the door verbs — a door already where it was asked to be did
 *   not move)
 * - `doorClosed` / `doorOpened` — `{itemName}` (D135: the EVENT of a door
 *   moving when the pack authored no `closedNarratives` / `openedNarratives`
 *   — deliberately flat, like `switchedOn`: a door worth writing about gets
 *   its own lines, and the state description is `look`'s to speak)
 * - `switchIsLatched` — `{itemName}` (D65: a one-way switch, thrown, asked to
 *   come back — refused by construction; this only flavors it)
 * - `handsAreNotEnough` — `{itemName}` (D63: `open X with hands` on a thing
 *   hands cannot open — the honest refusal to the engine's own "with what?")
 * - `handsDescription` — (none; D63: `inspect hands` — the one body part the
 *   catalog answers to deserves a sentence, not a shrug)
 * - `searchRoomSurvey` — `{itemNames}` (D62: casing the ROOM is a survey, not
 *   a sweep — it names what would reward going through properly and reveals
 *   nothing itself, so the per-thing discoveries stay discoveries)
 * - `searchRoomEmpty` — (none; D62: the room, cased, offering nothing worth
 *   going through)
 * - `searchWhileShut` — `{itemName}` (D53: you cannot go through a thing whose
 *   door is still closed — the refusal that stops `search` reaching past a lid
 *   the catalog already respects)
 * - `searchSeenThrough` — `{itemName}`, `{itemNames}` (D253: the same shut
 *   door, of mesh or glass — the search still cannot go through it, and
 *   names what can be seen through it; `{itemNames}` is a sentence list)
 * - `seenBehindShutDoor` — `{itemName}`, `{containerName}` (D253: a hand
 *   aimed at a thing seen through a shut see-through door — `{containerName}`
 *   arrives as a sentence names it, article and all; the thing's own
 *   `unreachableNarratives` outrank it)
 * - `searchFoundNothing` — `{itemName}` (D50: searching turned up nothing, and
 *   saying so specifically is what makes the verb worth typing elsewhere; a
 *   thing may answer in its own words with `searchNarratives`. Spoken while
 *   the player carries something — its "nothing you did not already have"
 *   measures the find against the hands)
 * - `searchFoundNothingEmptyHanded` — `{itemName}` (D229, E18: the same
 *   nothing, found with nothing in the hands to measure it against — the
 *   play-test heard the line above as an inventory line when the hands were
 *   empty, because it was one)
 * - `searchFoundOnlyInSight` — `{itemName}`, `{itemNames}` (D247: nothing
 *   hidden, and the thing still holds what anyone can see — named, because
 *   "nothing you did not already have" is false of a kit with a key still
 *   in it; `{itemNames}` is a sentence list, as `droppedHere`'s is)
 * - `socialInput` — (none; D29)
 * - `statementNotUnderstood` — (none; D29)
 * - `structureFallback` — `{tokens}`, `{suggestions}`
 * - `takeAcknowledgement` — `{itemName}`
 * - `unboundPreposition` — `{intent}`, `{tokens}`, `{suggestions}`
 * - `unknownIntent` — `{intent}`
 * - `unsupportedIntent` — `{intent}`, `{verb}` (D35)
 * - `unsupportedIntentOnTarget` — `{intent}`, `{itemName}`, `{verb}` (D35;
 *   D44 warns that a frame reading `{verb} the {itemName}` is ungrammatical
 *   for intransitive verbs — name the thing first)
 * - `verbCouldNotCarry` — `{intent}`, `{tokens}`, `{suggestions}`
 * - `verbUnanswered` — `{intent}`, `{itemName}`, `{verb}` (D196: an authored
 *   verb aimed at a thing that has no arm for it, or none whose conditions
 *   hold — the `unsupportedIntentOnTarget` shape for a verb that CAN act)
 * - `verbNeedsTarget` — `{intent}`, `{suggestions}`
 * - `verbStandsAlone` — `{intent}`, `{tokens}`, `{suggestions}`
 * - `waitAcknowledgement` — (none; D52: what letting a turn pass sounds like.
 *   A pack with a clock running should claim this one)
 * - `capacityExceeded` — `{itemName}` (R25: a take refused because the hands
 *   are full; `drop` is the relief valve)
 * - `dropAcknowledgement` — `{itemName}` (R25: a successful drop; an item's
 *   authored `dropNarratives` outrank it, the takeNarratives pattern)
 * - `searchRoomDroppedAlso` — `{itemNames}` (R25, Calder's §1.2: the survey's
 *   ADDENDUM when the room also holds things the player set down)
 * - `searchRoomBarredAlso` — `{itemNames}` (R30, Calder's barred table: the
 *   survey's addendum for searchworthy things that are SEEN and out of reach
 *   — never in the survey's own list, because "worth going through" is an
 *   OFFER and a barred thing gets its own clause with its own verb;
 *   barrier-agnostic for the same reason `outOfReach` is)
 * - `searchRoomBarredOnly` — `{itemNames}` (R30: everything searchworthy
 *   here is barred — the slot that keeps `searchRoomEmpty` honest when the
 *   room's contents are all behind the glass)
 * - `searchRoomDroppedOnly` — `{itemNames}` (R25, Calder's §1.2: the room
 *   authors nothing searchworthy but holds dropped things — the slot that
 *   exists so `searchRoomEmpty` can never lie; the dropped set participates
 *   in CHOOSING the slot, never decorates the one already chosen)
 * - `droppedHere` — `{itemNames}` (D226, E8: spoken after a room's
 *   description, on arrival and on `look`, naming what the player set down
 *   in it — the engine's own sentence, so no pack has to author "here,
 *   where you left it"; `{itemNames}` arrives as a sentence list, each name
 *   with the article it takes: "the flask and the rope")
 * - `nothingToRepeat` — (none; D226, E12: `again` with no earlier input to
 *   repeat)
 * - `notCarried` — `{itemName}` (R25: dropping a thing you do not hold; the
 *   refusal never mentions searching)
 * - `notWearable` — `{itemName}` (D143: `wear` on a thing with no `equippable`)
 * - `alreadyWorn` — `{itemName}` (D143: it is already in its slot)
 * - `slotOccupied` — `{itemName}`, `{wornItemName}` (D143: the slot holds
 *   something else — take that off first)
 * - `notWorn` — `{itemName}` (D143: `take off` on a thing not worn)
 * - `pluralSetNamed` — `{itemNames}` (D217: looking at a SET the pack gave no
 *   group line for — the members named in one sentence; a group's own
 *   `description` outranks it)
 * - `pluralOneAtATime` — `{intent}` (D217: any verb but looking, aimed at a
 *   set, under the pack's `set` policy — refused, and nothing happens to the
 *   things)
 * - `equipped` / `unequipped` — `{itemName}` (D143: the plain event lines
 *   when the item authors none)
 * - `wornMarker` — `{itemName}` (D143 addendum, Calder's finding: how a worn
 *   thing's name reads inside `inventoryList`'s `{itemNames}`, so the one
 *   question the inventory is asked — is it on me? — is answered where it is
 *   asked. A pack with no equipment never hears it)
 * - `itemSpent` — `{itemName}` (D152: an act a spent thing refuses — the
 *   strap gone, the knife broken; an item's `spentRefusalNarratives` outrank
 *   it)
 * - `actorOutOfReach` — `{itemName}` (D232: a verb other than speech aimed
 *   at an actor the player can HEAR and not reach — one heard through its
 *   `audibleFrom` — `{itemName}` being the actor's name; an actor's authored
 *   `unreachableNarratives` outrank it. `ask` and `talk` are untouched: the
 *   whole point of earshot is that they work)
 * - `verbOnActor` — `{intent}`, `{itemName}`, `{verb}` (D232: an item verb
 *   aimed at an actor — `take the woman`, `open the guard` — refused because
 *   an actor is not a thing; an actor's authored `verbNarratives` for the
 *   verb outrank it. `{itemName}` is the actor's name)
 * - `outOfReach` — `{itemName}` (R29: acting on a thing the player can see
 *   and not reach — `reachableWhen` unmet; an item's authored
 *   `unreachableNarratives` outrank it. Observation is untouched: the line
 *   speaks only for verbs that would cross the barrier)
 * - `retakeAcknowledgement` — `{itemName}` (D98: taking back a thing the
 *   player set down. `takeNarratives` were authored for the item's PLACEMENT
 *   and speak only on the FIRST acquisition — the record's write-once
 *   semantics, heard; an item's authored `retakeNarratives` outrank this)
 * - `storyHasEnded` — (none; R11: the answer to a player who keeps typing
 *   after a TERMINAL ending. A specific ending's own `refusalNarratives`
 *   outrank this slot; the slot is the voice for endings that authored none)
 * - `notAFoe` — `{intent}`, `{itemName}`, `{verb}` (D234: `attack` aimed at
 *   an actor with no `combat` block — "The woman is not something you
 *   fight"; the actor's `verbNarratives.attack` outrank it. `{itemName}` is
 *   the actor's name)
 * - `foeAlreadyDefeated` — `{itemName}` (D234: `attack` on a body that has
 *   already fallen)
 * - `notAWeapon` — `{itemName}` (D234: `attack … with X` where the carried X
 *   declares no `weapon`)
 * - `weaponNotWielded` — `{itemName}` (D234: `attack … with X` where X is
 *   `equippable` and not equipped — D143's rule, a wieldable thing must be
 *   in the hand)
 * - `strikeLanded` — `{damage}`, `{health}`, `{itemName}` (D234: the
 *   player's strike cost the foe something — `{damage}` what it cost,
 *   `{health}` what the foe has left; the foe's `hurtNarratives` outrank it)
 * - `strikeAbsorbed` — `{itemName}` (D234: the player's strike, turned
 *   entirely by the foe's `defense`)
 * - `foeDefeated` — `{itemName}` (D234: the foe falls; its
 *   `defeatNarratives` outrank it)
 * - `foeStrikes` — `{itemName}` (D234: the foe strikes on the tick; its
 *   `attackNarratives` outrank it)
 * - `playerHurt` — `{damage}`, `{health}`, `{itemName}` (D234: the fact of
 *   the foe's strike, after its line — what it cost and what is left; a
 *   pack re-voices it as a reading. `{itemName}` is the foe's name)
 * - `playerUnhurt` — `{itemName}` (D234: the foe's strike, turned entirely
 *   by what the player wears)
 * - `giftRefused` — `{itemName}` (D235: a thing offered to a body that has
 *   no `giveInteractions` arm for it — the actor's own
 *   `refusesGiftsNarratives` outrank it. `{itemName}` is the THING offered)
 * - `missingRecipient` — `{intent}`, `{verb}`, `{Verb}`, `{itemName}` (D235:
 *   `give <thing>` with nobody named, in a game where somebody takes gifts —
 *   parked as a question, so a bare name answers it: "Give the film to
 *   whom?")
 * - `notInPlaceNamed` — `{itemName}` (D242: `take <thing> from
 *   <place>` where the thing is not in the place named — not in that
 *   container, not carried by that body; nothing moves)
 * - `stockEmpty` — `{itemName}` (D244: `take` from a stock whose draw has
 *   given everything it counts — the draw's own `emptyNarratives` outrank it)
 * - `stockFull` — `{itemName}` (D244: `take` from a stock when what it gives
 *   would pass its counter's ceiling — nothing moves; the draw's own
 *   `fullNarratives` outrank it)
 * - `orderRefused` — `{intent}`, `{itemName}`, `{verb}` (D252: an order —
 *   `tell <body> to <clause>` — the body has no `orders` arm for, or none
 *   whose conditions hold; the actor's own `orderFallbackNarratives`
 *   outrank it. `{itemName}` is the body's name, `{verb}` the verb it was
 *   told)
 */
export const defaultResponseSlotSchema = z.enum([
	'actorOutOfReach',
	'alreadyHeld',
	'answerDoesNotServe',
	'answerNotUnderstood',
	'askDeflection',
	'bareNounPrompt',
	'blankInput',
	'capacityExceeded',
	'chainAbandoned',
	'clarificationQuestion',
	'conditionUnmet',
	'correctionConfirm',
	'correctionKeepLabel',
	'correctionKept',
	'correctionProceedLabel',
	'dropAcknowledgement',
	'droppedHere',
	'entityNotFound',
	'entityNotVisible',
	'entityUnknown',
	'exitLeadsOutward',
	'gibberishInput',
	'help',
	'impliedInstrument',
	'inventoryEmpty',
	'inventoryList',
	'malformedCommand',
	'missingDirection',
	'missingInstrument',
	'missingTarget',
	'noExitThatWay',
	'noIntentRecognized',
	'noMatchingInteraction',
	'noPathToThat',
	'notCarried',
	'nothingToRepeat',
	'notOpenable',
	'nothingOpensThat',
	'notPortable',
	'noQuestionToWithdraw',
	'loadAutomatic',
	'loadNothingKept',
	'loadNothingSaved',
	'loadRestored',
	'restartAnswers',
	'restartConfirm',
	'restartKeepLabel',
	'restartKept',
	'restartProceedLabel',
	'saveAutomatic',
	'saveNothingKept',
	'saveWritten',
	'outOfReach',
	'questionNotUnderstood',
	'questionSetAside',
	'questionWithdrawn',
	'retakeAcknowledgement',
	'searchFoundNothing',
	'searchFoundNothingEmptyHanded',
	'searchFoundOnlyInSight',
	'handsAreNotEnough',
	'handsDescription',
	'deviceIsDead',
	'searchRoomBarredAlso',
	'searchRoomBarredOnly',
	'searchRoomDroppedAlso',
	'searchRoomDroppedOnly',
	'searchRoomEmpty',
	'searchRoomSurvey',
	'alreadyWorn',
	'equipped',
	'notWearable',
	'notWorn',
	'pluralOneAtATime',
	'pluralSetNamed',
	'slotOccupied',
	'unequipped',
	'wornMarker',
	'itemSpent',
	'doorAlreadyClosed',
	'doorAlreadyOpen',
	'doorClosed',
	'doorOpened',
	'switchedOff',
	'switchAlreadyOff',
	'switchAlreadyOn',
	'switchIsLatched',
	'switchedOn',
	'searchSeenThrough',
	'searchWhileShut',
	'seenBehindShutDoor',
	'socialInput',
	'statementNotUnderstood',
	'storyHasEnded',
	'structureFallback',
	'takeAcknowledgement',
	'unboundPreposition',
	'unknownIntent',
	'unsupportedIntent',
	'unsupportedIntentOnTarget',
	'verbCouldNotCarry',
	'verbNeedsTarget',
	'verbOnActor',
	'verbStandsAlone',
	'verbUnanswered',
	'waitAcknowledgement',
	'notAFoe',
	'foeAlreadyDefeated',
	'notAWeapon',
	'weaponNotWielded',
	'strikeLanded',
	'strikeAbsorbed',
	'foeDefeated',
	'foeStrikes',
	'playerHurt',
	'playerUnhurt',
	'giftRefused',
	'missingRecipient',
	'notInPlaceNamed',
	'stockEmpty',
	'stockFull',
	'orderRefused',
]);

/** One overridable engine response slot — see {@link defaultResponseSlotSchema}. */
export type DefaultResponseSlot = z.infer<typeof defaultResponseSlotSchema>;

/** The canonical slot list (runtime iteration; alphabetical). */
export const DEFAULT_RESPONSE_SLOTS = defaultResponseSlotSchema.options;

/**
 * A pack's overrides: any subset of slots → the authored line that replaces
 * the engine's built-in for that slot.
 */
/*
 * D30: one line, or an ordered POOL — played in sequence per session, holding
 * on the last (author the workhorse teach last; the joke first).
 *
 * D58 note: EVERY slot takes a pool now. From D31 to D58 an allowlist
 * (`SEQUENCED_RESPONSE_SLOTS`) refused pools on the slots whose usage counts
 * were not yet threaded — a pool there would have silently held at line one,
 * the dead-content defect D31 refuses. Threading the handler and rejection
 * seams made the carve-out empty, so the allowlist and its refinement are
 * DELETED rather than widened: one rule, no author-facing implementation
 * leak, and no drift test keeping a list honest.
 */
export const defaultResponseOverridesSchema = z
	.partialRecord(
		defaultResponseSlotSchema,
		z.union([narrativeLineSchema, z.array(narrativeLineSchema).min(1).readonly()]),
	)
	.readonly();

/** A pack's response overrides — see {@link defaultResponseOverridesSchema}. */
export type DefaultResponseOverrides = z.infer<typeof defaultResponseOverridesSchema>;
